'use client';

import { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { RefreshCw, Home } from 'lucide-react';
import { captureClientException } from '@/lib/posthog';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error(error);
    captureClientException(error, {
      error_boundary: 'route',
      digest: error.digest,
    });
  }, [error]);

  return (
    <section className="min-h-screen flex items-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto text-center">
        <div className="text-6xl mb-6">⚠️</div>
        <h1 className="text-4xl md:text-5xl font-semibold text-foreground mb-4 tracking-[-0.02em]">
          Something went wrong
        </h1>
        <p className="text-lg text-muted-foreground mb-10 leading-relaxed">
          We hit an unexpected error in the production line.
          Our team has been notified and is investigating.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="outline"
            size="lg"
            className="rounded-full"
            onClick={reset}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
          <Button
            variant="default"
            size="lg"
            className="rounded-full"
            href="/"
          >
            <Home className="w-4 h-4 mr-2" />
            Go Home
          </Button>
        </div>

        {error.digest && (
          <p className="text-xs text-muted-foreground mt-10">
            Error ID: {error.digest}
          </p>
        )}
      </div>
    </section>
  );
}
